'use client'

import posthog from 'posthog-js'
import { MIAOZI_GTM_ID } from '@/lib/google-tags'

// =============================================================================
// Analytics Configuration
// =============================================================================

export const analyticsConfig = {
  posthog: {
    enabled: Boolean(process.env.NEXT_PUBLIC_POSTHOG_KEY),
    apiKey: process.env.NEXT_PUBLIC_POSTHOG_KEY || '',
    apiHost: process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com'
  },
  gtm: {
    // GTM is loaded in layout.tsx with the same shared public ID.
    enabled: Boolean(MIAOZI_GTM_ID)
  }
}

// =============================================================================
// Event Types (for type-safety)
// =============================================================================

export type AnalyticsEventName =
  // Search events
  | 'search_performed'
  | 'search_result_clicked'
  // Dictionary events
  | 'word_viewed'
  | 'word_audio_played'
  | 'public_dictionary_entry_viewed'
  | 'dictionary_clean_route_clicked_from_search'
  | 'public_word_save_clicked'
  | 'hsk_page_viewed'
  | 'tocfl_page_viewed'
  | 'public_hsk_study_clicked'
  | 'public_tocfl_study_clicked'
  // Story events
  | 'story_started'
  | 'story_completed'
  | 'story_audio_played'
  | 'story_token_viewed'
  | 'story_grammar_viewed'
  // Monetization events
  | 'paywall_viewed'
  | 'paywall_cta_clicked'
  | 'premium_interest_submitted'
  | 'reader_limit_reached'
  | 'premium_chapter_locked'
  | 'checkout_started'
  | 'checkout_redirected'
  | 'portal_opened'
  | 'webhook_sync_state'
  // Collection events
  | 'collection_created'
  | 'item_bookmarked'
  | 'item_added_to_collection'
  | 'item_removed_from_collection'
  // Flashcard events
  | 'flashcard_study_started'
  | 'flashcard_reviewed'
  | 'flashcard_study_completed'
  // Today events
  | 'today_viewed'
  | 'today_review_clicked'
  | 'today_continue_reading_clicked'
  | 'today_start_path_clicked'
  | 'today_import_text_clicked'
  // Onboarding events
  | 'onboarding_started'
  | 'onboarding_setup_completed'
  | 'onboarding_skipped'
  | 'onboarding_prompt_dismissed'
  | 'onboarding_first_story_clicked'
  | 'onboarding_completed'
  // Reader events
  | 'reader_word_tapped'
  | 'reader_word_saved'
  | 'reader_sentence_saved'
  | 'reader_study_text_clicked'
  | 'reader_text_words_saved'
  | 'reader_import_completed'
  // Auth events
  | 'user_signed_in'
  | 'user_signed_out'
  // Navigation events
  | 'page_view'
  // Cross-product promotion
  | 'moyu_promo_impression'
  | 'moyu_promo_clicked'
  // Generic
  | 'button_clicked'
  | 'feature_used'

// =============================================================================
// Initialization State
// =============================================================================

let isPosthogInitialized = false

// =============================================================================
// GTM DataLayer Interface
// =============================================================================

interface DataLayerEvent {
  event: string
  [key: string]: unknown
}

function getWindow(): Window & { dataLayer?: DataLayerEvent[] } {
  return window as Window & { dataLayer?: DataLayerEvent[] }
}

/**
 * Push an event to GTM's dataLayer.
 * GTM must be loaded in layout.tsx for this to work.
 */
function pushToDataLayer(
  eventName: string,
  properties?: Record<string, unknown>
): void {
  if (typeof window === 'undefined') return

  const win = getWindow()
  win.dataLayer = win.dataLayer || []
  win.dataLayer.push({
    event: eventName,
    ...properties
  })
}

// =============================================================================
// PostHog Implementation
// =============================================================================

export function initPosthog(): void {
  if (
    isPosthogInitialized ||
    !analyticsConfig.posthog.enabled ||
    typeof window === 'undefined'
  ) {
    return
  }

  posthog.init(analyticsConfig.posthog.apiKey, {
    api_host: analyticsConfig.posthog.apiHost,
    // Capture pageviews manually for more control
    capture_pageview: false,
    // Capture page leaves for session tracking
    capture_pageleave: true,
    // Disable autocapture to reduce noise - we'll track explicitly
    autocapture: false,
    // Enable session recording if needed (disabled by default for privacy)
    disable_session_recording: true,
    // Persistence mode - use localStorage to avoid cookies if preferred
    persistence: 'localStorage',
    // Bootstrap with anonymous distinct_id
    bootstrap: {
      distinctID: undefined // Let PostHog generate one
    }
  })

  isPosthogInitialized = true
}

function trackPosthog(
  eventName: string,
  properties?: Record<string, unknown>
): void {
  if (!analyticsConfig.posthog.enabled || !isPosthogInitialized) return
  posthog.capture(eventName, properties)
}

function identifyPosthog(
  userId: string,
  traits?: Record<string, unknown>
): void {
  if (!analyticsConfig.posthog.enabled || !isPosthogInitialized) return
  posthog.identify(userId, traits)
}

function resetPosthog(): void {
  if (!analyticsConfig.posthog.enabled || !isPosthogInitialized) return
  posthog.reset()
}

// =============================================================================
// Unified Analytics API
// =============================================================================

/**
 * Initialize all enabled analytics providers.
 * Call this once when the app loads.
 * Note: GTM is loaded in layout.tsx, so no initialization needed here.
 */
export function initAnalytics(): void {
  initPosthog()
}

/**
 * Track a custom event to all enabled providers.
 * Events are pushed to GTM's dataLayer and also sent to PostHog if enabled.
 */
export function trackEvent(
  eventName: AnalyticsEventName | string,
  properties?: Record<string, unknown>
): void {
  // Push to GTM dataLayer (for GA4 and other GTM tags)
  if (analyticsConfig.gtm.enabled) {
    pushToDataLayer(eventName, properties)
  }

  // Also track in PostHog
  trackPosthog(eventName, properties)
}

/**
 * Track a page view to all enabled providers.
 */
export function trackPageView(path: string, title?: string): void {
  const properties = {
    path,
    page_path: path,
    page_title:
      title || (typeof document !== 'undefined' ? document.title : ''),
    page_location: typeof window !== 'undefined' ? window.location.href : '',
    referrer: typeof document !== 'undefined' ? document.referrer : ''
  }

  // Push to GTM dataLayer
  if (analyticsConfig.gtm.enabled) {
    pushToDataLayer('page_view', properties)
  }

  // Track in PostHog
  trackPosthog('$pageview', properties)
}

/**
 * Identify a user across all enabled providers.
 * Call this after user logs in.
 */
export function identifyUser(
  userId: string,
  traits?: Record<string, unknown>
): void {
  identifyPosthog(userId, traits)

  // Push user_id to GTM dataLayer for GA4
  if (analyticsConfig.gtm.enabled) {
    pushToDataLayer('user_identified', {
      user_id: userId,
      ...traits
    })
  }
}

/**
 * Reset user identity (on logout).
 */
export function resetUser(): void {
  resetPosthog()

  // Clear user_id in GTM dataLayer
  if (analyticsConfig.gtm.enabled) {
    pushToDataLayer('user_logout', { user_id: null })
  }
}

// =============================================================================
// Convenience Tracking Functions
// =============================================================================

export const analytics = {
  // Search
  searchPerformed: (query: string, mode: 'chinese' | 'english') =>
    trackEvent('search_performed', { query, mode }),

  searchResultClicked: (entryId: number, position: number) =>
    trackEvent('search_result_clicked', { entry_id: entryId, position }),

  // Dictionary
  wordViewed: (entryId: number, word: string) =>
    trackEvent('word_viewed', { entry_id: entryId, word }),

  wordAudioPlayed: (entryId: number, word: string) =>
    trackEvent('word_audio_played', { entry_id: entryId, word }),

  publicDictionaryEntryViewed: (entryId: number, word: string) =>
    trackEvent('public_dictionary_entry_viewed', { entry_id: entryId, word }),

  dictionaryCleanRouteClickedFromSearch: (
    entryId: number,
    surface: 'split_view' | 'native_link' = 'split_view'
  ) =>
    trackEvent('dictionary_clean_route_clicked_from_search', {
      entry_id: entryId,
      surface
    }),

  publicWordSaveClicked: (entryId: number, word: string) =>
    trackEvent('public_word_save_clicked', { entry_id: entryId, word }),

  hskPageViewed: (level?: number) =>
    trackEvent('hsk_page_viewed', { level: level ?? null }),

  tocflPageViewed: (level?: number) =>
    trackEvent('tocfl_page_viewed', { level: level ?? null }),

  publicHskStudyClicked: (level: number) =>
    trackEvent('public_hsk_study_clicked', { level }),

  publicTocflStudyClicked: (level: number) =>
    trackEvent('public_tocfl_study_clicked', { level }),

  // Stories
  storyStarted: (storyId: string, title: string) =>
    trackEvent('story_started', { story_id: storyId, title }),

  storyCompleted: (storyId: string, readingTimeSeconds: number) =>
    trackEvent('story_completed', {
      story_id: storyId,
      reading_time_seconds: readingTimeSeconds
    }),

  storyAudioPlayed: (storyId: string, sentenceIndex: number) =>
    trackEvent('story_audio_played', {
      story_id: storyId,
      sentence_index: sentenceIndex
    }),

  storyTokenViewed: (word: string, entryId?: number) =>
    trackEvent('story_token_viewed', {
      word,
      entry_id: entryId
    }),

  storyGrammarViewed: (point: string) =>
    trackEvent('story_grammar_viewed', { point }),

  // Monetization
  paywallViewed: (
    paywallType: string,
    surface: string,
    details?: Record<string, unknown>
  ) =>
    trackEvent('paywall_viewed', {
      paywall_type: paywallType,
      surface,
      ...details
    }),

  paywallCtaClicked: (
    paywallType: string,
    surface: string,
    details?: Record<string, unknown>
  ) =>
    trackEvent('paywall_cta_clicked', {
      paywall_type: paywallType,
      surface,
      ...details
    }),

  premiumInterestSubmitted: (
    paywallType: string,
    surface: string,
    details?: Record<string, unknown>
  ) =>
    trackEvent('premium_interest_submitted', {
      paywall_type: paywallType,
      surface,
      ...details
    }),

  readerLimitReached: (
    currentUsage: number,
    limit: number,
    surface: string,
    details?: Record<string, unknown>
  ) =>
    trackEvent('reader_limit_reached', {
      current_usage: currentUsage,
      limit,
      surface,
      ...details
    }),

  premiumChapterLocked: (
    storySlug: string,
    chapterNumber: number,
    surface: string,
    details?: Record<string, unknown>
  ) =>
    trackEvent('premium_chapter_locked', {
      story_slug: storySlug,
      chapter_number: chapterNumber,
      surface,
      ...details
    }),

  checkoutStarted: (
    plan: 'weekly' | 'yearly',
    paywallType: string,
    surface: string,
    details?: Record<string, unknown>
  ) =>
    trackEvent('checkout_started', {
      plan,
      paywall_type: paywallType,
      surface,
      ...details
    }),

  checkoutRedirected: (
    plan: 'weekly' | 'yearly',
    paywallType: string,
    surface: string,
    details?: Record<string, unknown>
  ) =>
    trackEvent('checkout_redirected', {
      plan,
      paywall_type: paywallType,
      surface,
      ...details
    }),

  portalOpened: (surface: string, details?: Record<string, unknown>) =>
    trackEvent('portal_opened', {
      surface,
      ...details
    }),

  webhookSyncState: (
    state: 'processed' | 'duplicate' | 'rejected' | 'failed',
    eventType?: string,
    details?: Record<string, unknown>
  ) =>
    trackEvent('webhook_sync_state', {
      state,
      event_type: eventType ?? null,
      ...details
    }),

  // Collections
  collectionCreated: (collectionId: string, name: string) =>
    trackEvent('collection_created', { collection_id: collectionId, name }),

  itemBookmarked: (entryId: number, isBookmarked: boolean) =>
    trackEvent('item_bookmarked', {
      entry_id: entryId,
      is_bookmarked: isBookmarked
    }),

  itemAddedToCollection: (collectionId: string, entryId: number) =>
    trackEvent('item_added_to_collection', {
      collection_id: collectionId,
      entry_id: entryId
    }),

  // Flashcards
  flashcardStudyStarted: (collectionId: string, cardCount: number) =>
    trackEvent('flashcard_study_started', {
      collection_id: collectionId,
      card_count: cardCount
    }),

  flashcardReviewed: (
    entryId: number,
    rating: number,
    responseTimeMs: number
  ) =>
    trackEvent('flashcard_reviewed', {
      entry_id: entryId,
      rating,
      response_time_ms: responseTimeMs
    }),

  flashcardStudyCompleted: (
    collectionId: string,
    cardsReviewed: number,
    sessionDurationSeconds: number
  ) =>
    trackEvent('flashcard_study_completed', {
      collection_id: collectionId,
      cards_reviewed: cardsReviewed,
      session_duration_seconds: sessionDurationSeconds
    }),

  // Today
  todayViewed: (signedIn: boolean) =>
    trackEvent('today_viewed', { signed_in: signedIn }),

  todayReviewClicked: (dueCount: number) =>
    trackEvent('today_review_clicked', { due_count: dueCount }),

  todayContinueReadingClicked: (sourceType: string | null) =>
    trackEvent('today_continue_reading_clicked', {
      source_type: sourceType
    }),

  todayStartPathClicked: (pathId: string) =>
    trackEvent('today_start_path_clicked', { path_id: pathId }),

  todayImportTextClicked: () => trackEvent('today_import_text_clicked'),

  // Onboarding
  onboardingStarted: () => trackEvent('onboarding_started'),

  onboardingSetupCompleted: (goal: string, level: string, storySlug: string) =>
    trackEvent('onboarding_setup_completed', {
      goal,
      level,
      story_slug: storySlug
    }),

  onboardingSkipped: () => trackEvent('onboarding_skipped'),

  onboardingPromptDismissed: () => trackEvent('onboarding_prompt_dismissed'),

  onboardingFirstStoryClicked: (storySlug: string) =>
    trackEvent('onboarding_first_story_clicked', {
      story_slug: storySlug
    }),

  onboardingCompleted: (storySlug: string | undefined) =>
    trackEvent('onboarding_completed', {
      story_slug: storySlug ?? null
    }),

  // Reader
  readerWordTapped: (
    entryId: number | null,
    sourceType: string,
    hasGlossaryEntry: boolean
  ) =>
    trackEvent('reader_word_tapped', {
      entry_id: entryId,
      source_type: sourceType,
      has_glossary_entry: hasGlossaryEntry
    }),

  readerWordSaved: (entryId: number, sourceType: string) =>
    trackEvent('reader_word_saved', {
      entry_id: entryId,
      source_type: sourceType
    }),

  readerSentenceSaved: (sourceType: string, hasTranslation: boolean) =>
    trackEvent('reader_sentence_saved', {
      source_type: sourceType,
      has_translation: hasTranslation
    }),

  readerStudyTextClicked: (sourceType: string, candidateCount: number) =>
    trackEvent('reader_study_text_clicked', {
      source_type: sourceType,
      candidate_count: candidateCount
    }),

  readerTextWordsSaved: (sourceType: string, count: number) =>
    trackEvent('reader_text_words_saved', {
      source_type: sourceType,
      count
    }),

  readerImportCompleted: (
    sourceType: string,
    blockCount: number,
    glossaryCount: number,
    fileType?: string | null
  ) =>
    trackEvent('reader_import_completed', {
      source_type: sourceType,
      block_count: blockCount,
      glossary_count: glossaryCount,
      file_type: fileType ?? null
    }),

  // Auth
  userSignedIn: (method: 'google' | 'email' | 'anonymous') =>
    trackEvent('user_signed_in', { method }),

  userSignedOut: () => trackEvent('user_signed_out'),

  // Generic
  buttonClicked: (buttonName: string, context?: string) =>
    trackEvent('button_clicked', { button_name: buttonName, context }),

  featureUsed: (featureName: string, details?: Record<string, unknown>) =>
    trackEvent('feature_used', { feature_name: featureName, ...details })
}
